import React, { useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import {
  useAttachExperienceOfferUrgencyTag,
  useDetachExperienceOfferUrgencyTag,
  useGetExperienceUrgencyTags,
} from '~/hooks/apis/experiences';

import { ExperienceUrgencyTag } from '~/services/ExperiencesService';

import getErrorMessage from '~/utils/getErrorMessage';

const EMPTY_URGENCY_TAGS: Array<ExperienceUrgencyTag> = [];

interface Props {
  offerId: string;
  urgencyTags?: Array<ExperienceUrgencyTag>;
  disabled: boolean;
}

function TagPreview({ tag }: { tag: ExperienceUrgencyTag }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        bgcolor: tag.bgColor,
        color: tag.textColor,
        typography: 'caption',
        fontWeight: 700,
        textTransform: 'uppercase',
      }}
    >
      {tag.text}
    </Box>
  );
}

export default function ExperienceUrgencyTags({ offerId, urgencyTags, disabled }: Props) {
  const sourceTags = urgencyTags ?? EMPTY_URGENCY_TAGS;
  const [tags, setTags] = useState<Array<ExperienceUrgencyTag>>(() => sourceTags);
  const [selectedTagId, setSelectedTagId] = useState('');
  const [tagPendingDetach, setTagPendingDetach] = useState<ExperienceUrgencyTag | null>(null);
  const [saveSucceeded, setSaveSucceeded] = useState(false);
  const catalogueQuery = useGetExperienceUrgencyTags();
  const attachUrgencyTag = useAttachExperienceOfferUrgencyTag();
  const detachUrgencyTag = useDetachExperienceOfferUrgencyTag();

  useEffect(() => {
    setTags(sourceTags);
    setSelectedTagId('');
    setSaveSucceeded(false);
  }, [offerId, sourceTags]);

  const availableTags = useMemo(() => {
    const attachedTagIds = new Set(tags.map((tag) => tag.id));
    return (catalogueQuery.data ?? EMPTY_URGENCY_TAGS).filter((tag) => !attachedTagIds.has(tag.id));
  }, [catalogueQuery.data, tags]);
  const mutationError = attachUrgencyTag.error ?? detachUrgencyTag.error;
  const error = mutationError ?? catalogueQuery.error;
  const isPending = attachUrgencyTag.isPending || detachUrgencyTag.isPending;

  const attachTag = async () => {
    if (!selectedTagId) return;

    try {
      const tag = await attachUrgencyTag.mutateAsync({ offerId, urgencyTagId: selectedTagId });
      setTags((currentTags) => [...currentTags, tag]);
      setSelectedTagId('');
      setSaveSucceeded(true);
    } catch {
      // The mutation error is shown in the alert above the form.
    }
  };

  const confirmDetachTag = async () => {
    if (!tagPendingDetach) return;

    try {
      await detachUrgencyTag.mutateAsync({ offerId, urgencyTagId: tagPendingDetach.id });
      setTags((currentTags) => currentTags.filter((tag) => tag.id !== tagPendingDetach.id));
      setTagPendingDetach(null);
      setSaveSucceeded(true);
    } catch {
      // The mutation error is shown in the alert above the form.
    }
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
        Urgency tags
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Displayed on experience images during checkout only.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(error)}
        </Alert>
      )}
      {saveSucceeded && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Urgency tags saved
        </Alert>
      )}

      <Stack spacing={1} divider={<Divider flexItem />}>
        {tags.map((tag) => (
          <Stack key={tag.id} direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
            <TagPreview tag={tag} />
            <IconButton
              aria-label={`Detach urgency tag ${tag.text}`}
              onClick={() => setTagPendingDetach(tag)}
              disabled={disabled || isPending}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ))}
      </Stack>

      {!tags.length && (
        <Typography variant="body2" color="text.secondary">
          No urgency tags attached
        </Typography>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }} alignItems={{ sm: 'center' }}>
        <FormControl
          fullWidth
          size="small"
          disabled={disabled || isPending || catalogueQuery.isLoading || !availableTags.length}
        >
          <InputLabel id="urgency-tag-select-label">Urgency tag</InputLabel>
          <Select
            labelId="urgency-tag-select-label"
            label="Urgency tag"
            value={selectedTagId}
            onChange={(event) => setSelectedTagId(event.target.value)}
          >
            {availableTags.map((tag) => (
              <MenuItem key={tag.id} value={tag.id}>
                {tag.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="text"
          startIcon={catalogueQuery.isLoading ? <CircularProgress size={16} /> : <AddIcon />}
          onClick={() => void attachTag()}
          disabled={disabled || isPending || !selectedTagId}
        >
          {attachUrgencyTag.isPending ? 'Attaching…' : 'Attach tag'}
        </Button>
      </Stack>

      <Dialog open={!!tagPendingDetach} onClose={() => setTagPendingDetach(null)}>
        <DialogTitle>Detach urgency tag?</DialogTitle>
        <DialogContent>
          <Typography>Remove “{tagPendingDetach?.text || 'this urgency tag'}” from this experience?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagPendingDetach(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void confirmDetachTag()} disabled={isPending}>
            {detachUrgencyTag.isPending ? 'Detaching…' : 'Detach'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
